import { useTranslation } from "react-i18next";
import type { AnalyticsSummaryDto } from "@normax-performance/shared-types";

export interface ZoneDistributionProps {
  summary: AnalyticsSummaryDto | undefined;
  isLoading: boolean;
}

/**
 * Compact segmented horizontal bar (docs/.../FORENSIC_PRODUCT_BRIEF.md §7
 * evaluated a donut and rejected it; this slice's spec §8 asks for the same
 * — a single stacked bar over ELIGIBLE workers only, exact counts alongside,
 * never a decorative gauge). Non-eligible workers are explicitly excluded
 * from the bar itself (the backend contract already separates them) and
 * surfaced as a short honest note instead of silently folded in or hidden.
 */
export function ZoneDistribution({ summary, isLoading }: ZoneDistributionProps) {
  const { t } = useTranslation("analytics");

  if (isLoading) {
    return (
      <div className="nx-zone-distribution">
        <span className="nx-skeleton" style={{ width: "100%", height: "10px" }} />
      </div>
    );
  }

  if (!summary) return null;

  const { below90Count, between90And100Count, above100Count, eligibleWorkerCount } = summary.workers;
  const notEligibleCount = summary.workers.recordedWorkerCount - eligibleWorkerCount;
  const total = below90Count + between90And100Count + above100Count;

  const pct = (count: number) => (total > 0 ? (count / total) * 100 : 0);

  return (
    <div className="nx-zone-distribution">
      <p className="nx-zone-distribution__title">{t("zoneDistribution.title")}</p>
      <div
        className="nx-zone-distribution__bar"
        role="img"
        aria-label={`${t("zoneDistribution.below")}: ${below90Count}, ${t("zoneDistribution.neutral")}: ${between90And100Count}, ${t("zoneDistribution.above")}: ${above100Count}`}
      >
        {below90Count > 0 ? (
          <div className="nx-zone-distribution__segment--below" style={{ width: `${pct(below90Count)}%` }} />
        ) : null}
        {between90And100Count > 0 ? (
          <div className="nx-zone-distribution__segment--neutral" style={{ width: `${pct(between90And100Count)}%` }} />
        ) : null}
        {above100Count > 0 ? (
          <div className="nx-zone-distribution__segment--above" style={{ width: `${pct(above100Count)}%` }} />
        ) : null}
      </div>
      <div className="nx-zone-distribution__legend">
        <span className="nx-zone-distribution__legend-item">
          <span className="nx-zone-distribution__swatch nx-zone-distribution__swatch--below" aria-hidden="true" />
          {t("zoneDistribution.below")} <span className="nx-zone-distribution__legend-count">{below90Count}</span>
        </span>
        <span className="nx-zone-distribution__legend-item">
          <span className="nx-zone-distribution__swatch nx-zone-distribution__swatch--neutral" aria-hidden="true" />
          {t("zoneDistribution.neutral")} <span className="nx-zone-distribution__legend-count">{between90And100Count}</span>
        </span>
        <span className="nx-zone-distribution__legend-item">
          <span className="nx-zone-distribution__swatch nx-zone-distribution__swatch--above" aria-hidden="true" />
          {t("zoneDistribution.above")} <span className="nx-zone-distribution__legend-count">{above100Count}</span>
        </span>
      </div>
      {notEligibleCount > 0 ? (
        <p className="nx-zone-distribution__note">{t("zoneDistribution.notEligibleNote", { count: notEligibleCount })}</p>
      ) : null}
    </div>
  );
}
